import { CSSProperties, ReactElement } from 'react';
import { Property } from 'csstype';

export interface TableRow {
    [field: string]: any;
}

export type DataTableCellFormat = 'default' | 'money' | 'date';

export interface DataTableColumn<TRow> {
    id?: string;
    field: keyof TRow;
    name: string | ReactElement;
    cellAlignment?: Property.TextAlign;
    cellStyle?: CSSProperties;
    columnCellClassName?: string;
    columnCellAlignment?: Property.TextAlign;
    width?: number;
    minWidth?: string | number;
    preventClick?: boolean;
    cellFormat?: DataTableCellFormat;
    suffix?: string;
    hideOn?: string[]; // 'sm','md','lg','xl'
    orderColumn?: string;
    hideOrderButton?: boolean;
    renderCell?: (row: TRow, column: DataTableColumn<TRow>, index: number) => ReactElement | string;
    excludeFromExport?: boolean;
    exportValue?: (row: TRow) => string | number | Date | null | undefined;
    /** Cabeçalho a usar no Excel quando difere do `name` (obrigatório quando `name` é JSX, ex.: para levar a unidade). */
    exportName?: string;
    headerNote?: string;
}

export interface OrderData<T> {
    field: keyof T;
    orderColumn?: string;
    isOrderAsc: boolean;
}

export interface ColumnOrder {
    i: number;
    field: string;
}

export interface ColumnWidth {
    width: number;
    field: string;
}

export interface DataTableData<TRow, TFilters = Record<string, unknown>> {
    hiddenColumns: string[];
    columnsOrder: ColumnOrder[];
    columnsWidth: ColumnWidth[];
    order: OrderData<TRow>;
    page: number;
    filters: TFilters;
    lastRowId: string | null;
}

export interface InfiniteScrollData {
    isLoading: boolean;
    hasMore: boolean;
    onLoadMore: () => void;
    // Bidirectional scroll support
    hasMorePrevious?: boolean;
    isLoadingPrevious?: boolean;
    onLoadPrevious?: () => void;
}

export const getColumnId = <TRow,>(col: DataTableColumn<TRow>): string =>
    col.id ?? (col.field as string);

export interface ExpandableRowConfig<TRow> {
    /** Renders the content inside the expanded row (full-width cell spanning all columns) */
    renderContent: (row: TRow, rowIndex: number) => ReactElement;
    /** Returns true if the given row is currently expanded */
    isExpanded: (row: TRow) => boolean;
    /** Called when the expand/collapse icon is clicked */
    onToggle: (row: TRow, rowIndex: number) => void;
    /** If provided, each item becomes a <tr> with <td> per column (aligns with headers). Takes priority over renderContent. colSpans merges adjacent columns (e.g. { photoUrl: 4 } spans 4 cols). chevronCell renders content in the expand chevron column (30px). Use fullContent to make an expanded row span the entire table width (chevron + all columns + action column). */
    renderExpandedCells?: (row: TRow, rowIndex: number) => { key: string; className?: string; chevronCell?: ReactElement; cells: Record<string, ReactElement | null>; colSpans?: Record<string, number>; fullContent?: ReactElement }[];
    /** Full-width footer row below expanded cells (e.g. "load more", "no results"). Only used with renderExpandedCells. */
    renderExpandedFooter?: (row: TRow, rowIndex: number) => ReactElement | null;
}
